"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const STEPS = ["announced", "login", "offer", "winner"] as const;

interface CtaProcessListProps {
  active: number;
  entered: boolean;
}

export default function CtaProcessList({ active, entered }: CtaProcessListProps) {
  const t = useTranslations("cta.steps");

  return (
    <ol className="flex w-full max-w-[480px] flex-col gap-3">
      {STEPS.map((key, index) => {
        const state = index === active ? "active" : index < active ? "done" : "pending";
        return (
          <li
            key={key}
            aria-current={state === "active" ? "step" : undefined}
            style={{ animationDelay: `${index * 100}ms` }}
            className={cn(
              "flex flex-wrap items-center gap-3.5 rounded-2xl border-[1.5px] px-[18px] py-4 transition-[background-color,border-color] duration-[400ms]",
              entered
                ? "animate-cta-slide motion-reduce:animate-none"
                : "translate-x-6 opacity-0 motion-reduce:translate-x-0 motion-reduce:opacity-100",
              state === "active"
                ? "border-brand-cream bg-brand-cream text-brand-navy"
                : "border-brand-track bg-white/[0.04] text-brand-cream",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-[15px] font-bold",
                state === "active" && "bg-brand-accent text-brand-navy",
                state === "done" && "bg-brand-track text-brand-cream",
                state === "pending" && "bg-brand-navy-2 text-brand-muted-dark",
              )}
            >
              {index + 1}
            </span>
            <span className="min-w-[140px] flex-1">
              <span className="block text-lg font-semibold leading-tight">{t(`${key}.label`)}</span>
              <span
                className={cn(
                  "mt-0.5 block text-sm",
                  state === "active" ? "text-brand-muted-light" : "text-brand-muted-dark",
                )}
              >
                {t(`${key}.sub`)}
              </span>
            </span>
            <span
              className={cn(
                "ml-auto text-sm font-bold tabular-nums",
                state === "active" ? "text-brand-accent-text" : "text-brand-placeholder",
              )}
            >
              {t(`${key}.tag`)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export const CTA_STEP_COUNT = STEPS.length;
